// Video Director preview lightbox: one overlay (not a modal) over its host, shared by every thumbnail.
// A thumbnail calls open(items, index, opener); items are the sibling images of its collection:
// { url, kind?: "video", seekSec?, name, description, actions?: [{ key, label, icon, primary, disabled, onClick }] }.

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { LuChevronLeft, LuChevronRight, LuSearch, LuX } from "react-icons/lu";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);

const PreviewContext = createContext(null);
export const usePreview = () => useContext(PreviewContext);

const FOCUSABLE = "button:not(:disabled), video[controls], [tabindex='0']";

function Lightbox({ items, start, opener, onClose }) {
  const [index, setIndex] = useState(start);
  const ref = useRef(null);
  const item = items[index];
  const go = (d) => setIndex((i) => (i + d + items.length) % items.length);

  useEffect(() => {
    ref.current.querySelector("button")?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [opener]);

  // Capture phase on the window (first in the path): Esc closes even while the <video> (which swallows keys) has focus.
  useEffect(() => {
    const esc = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [onClose]);

  const release = (e) => {
    if (document.activeElement === e.currentTarget) ref.current.focus({ preventScroll: true });
  };

  const onKeyDown = (e) => {
    e.stopPropagation(); // the Director's own shortcuts (frame stepping, Esc) stay out
    if (e.key === "ArrowLeft" && items.length > 1 && e.target.tagName !== "VIDEO") go(-1);
    else if (e.key === "ArrowRight" && items.length > 1 && e.target.tagName !== "VIDEO") go(1);
    else if (e.key === "Tab") {
      const nodes = [...ref.current.querySelectorAll(FOCUSABLE)];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === nodes[0]) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        nodes[0].focus();
      }
    }
  };

  return (
    <div
      ref={ref}
      className="add-video-modal__vd-preview"
      role="dialog"
      aria-label={item.name || t("Preview")}
      tabIndex={-1}
      style={{ outline: "none" }}
      onKeyDown={onKeyDown}
    >
      <div className="add-video-modal__vd-preview-scrim" role="presentation" onClick={onClose} />
      <button
        type="button"
        className="add-video-modal__vd-preview-close"
        aria-label={t("PreviewClose")}
        title={t("PreviewClose")}
        onClick={onClose}
      >
        <LuX size={18} />
      </button>
      <div className="add-video-modal__vd-preview-stage">
        {items.length > 1 && (
          <button
            type="button"
            className="add-video-modal__vd-preview-nav"
            aria-label={t("PreviewPrev")}
            onClick={() => go(-1)}
          >
            <LuChevronLeft size={22} />
          </button>
        )}
        {item.kind === "video" ? (
          <video
            key={item.url}
            src={item.seekSec ? `${item.url}#t=${item.seekSec.toFixed(2)}` : item.url}
            controls
            muted
            playsInline
            className="add-video-modal__vd-preview-media"
            // Chrome stops delivering key (and pointer) events to the page while the native controls hold focus,
            // so Esc would be lost: after any control action (seek / play / pause) take the focus back.
            onSeeked={release}
            onPlay={release}
            onPause={release}
          />
        ) : (
          <img key={item.url} src={item.url} alt={item.name || ""} className="add-video-modal__vd-preview-media" />
        )}
        {items.length > 1 && (
          <button
            type="button"
            className="add-video-modal__vd-preview-nav"
            aria-label={t("PreviewNext")}
            onClick={() => go(1)}
          >
            <LuChevronRight size={22} />
          </button>
        )}
      </div>
      <div className="add-video-modal__vd-preview-caption">
        {item.name && <div className="add-video-modal__vd-preview-name">{item.name}</div>}
        {item.description && (
          <div className="add-video-modal__vd-preview-desc">{item.description}</div>
        )}
        {items.length > 1 && (
          <div className="add-video-modal__vd-preview-count" aria-live="polite">
            {t("PreviewOf", { n: index + 1, total: items.length })}
          </div>
        )}
        {item.actions?.length > 0 && (
          <div className="add-video-modal__vd-actions" style={{ justifyContent: "center" }}>
            {item.actions.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`add-video-modal__btn add-video-modal__btn--${a.primary ? "primary" : "secondary"}`}
                style={{ fontSize: 12, padding: "4px 10px", display: "flex", alignItems: "center", gap: 4 }}
                disabled={a.disabled}
                onClick={() => {
                  onClose();
                  a.onClick();
                }}
              >
                {a.icon && <a.icon size={12} />}
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Wrap the part of the tree whose nearest positioned ancestor the lightbox should cover. */
export function PreviewHost({ children }) {
  const [open, setOpen] = useState(null); // { items, index, opener }
  const ctx = useRef({ open: (items, index, opener) => setOpen({ items, index, opener }) });
  return (
    <PreviewContext.Provider value={ctx.current}>
      {children}
      {open && (
        <Lightbox
          items={open.items}
          start={open.index}
          opener={open.opener}
          onClose={() => setOpen(null)}
        />
      )}
    </PreviewContext.Provider>
  );
}

const stop = (e) => e.stopPropagation();

/** The hover/focus "Preview" button; place it inside a positioned thumbnail. */
export function PreviewButton({ items, index }) {
  const pv = usePreview();
  if (!pv || !items?.[index]) return null;
  const open = (e) => {
    e.stopPropagation();
    pv.open(items, index, e.currentTarget);
  };
  // A span, not a button: thumbnails often sit inside a selectable <button>.
  return (
    <span
      role="button"
      tabIndex={0}
      className="add-video-modal__vd-pv-btn"
      aria-label={`${t("Preview")}${items[index].name ? `: ${items[index].name}` : ""}`}
      title={t("Preview")}
      onClick={open}
      onKeyDown={(e) => {
        stop(e);
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open(e);
        }
      }}
    >
      <LuSearch size={16} />
    </span>
  );
}

/**
 * Positioned wrapper for a thumbnail: the Preview button, and (clickable) a click on the image opens it
 * too. Leave `clickable` off where the thumbnail itself selects something.
 */
export function PreviewThumb({ preview, clickable, className, children }) {
  const pv = usePreview();
  const live = pv && preview?.items?.[preview.index];
  return (
    <span
      className={`add-video-modal__vd-pv-wrap${className ? ` ${className}` : ""}`}
      style={live && clickable ? { cursor: "zoom-in" } : undefined}
      onClick={
        live && clickable
          ? (e) =>
              pv.open(
                preview.items,
                preview.index,
                e.currentTarget.querySelector(".add-video-modal__vd-pv-btn") || e.currentTarget
              )
          : undefined
      }
    >
      {children}
      <PreviewButton items={preview?.items} index={preview?.index} />
    </span>
  );
}
